import React from 'react';
import { Pill, Clock, CheckCircle2, AlertCircle } from 'lucide-react';

interface Medication {
  id: string;
  name: string;
  dosage: string;
  frequency: string;
  next_dose?: string;
  is_overdue?: boolean;
  last_taken?: string;
}

interface MedicationReminderWidgetProps {
  medications: Medication[];
  isLoading?: boolean;
}

export function MedicationReminderWidget({
  medications,
  isLoading,
}: MedicationReminderWidgetProps): React.ReactElement {
  if (isLoading) {
    return (
      <div className="space-y-3">
        {[1, 2, 3].map((i) => (
          <div key={i} className="flex items-center gap-3 animate-pulse">
            <div className="w-10 h-10 bg-gray-200 rounded-lg" />
            <div className="flex-1">
              <div className="h-4 bg-gray-200 rounded w-2/3 mb-1" />
              <div className="h-3 bg-gray-200 rounded w-1/2" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (medications.length === 0) {
    return (
      <div className="text-center py-6">
        <Pill className="w-10 h-10 mx-auto mb-2 text-gray-300" />
        <p className="text-sm text-warm-gray">No active medications</p>
        <p className="text-xs text-gray-400 mt-1">
          Add medications to track them here
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {medications.map((med) => (
        <div
          key={med.id}
          className={`
            flex items-center gap-3 p-3 rounded-lg border
            ${med.is_overdue 
              ? 'border-red-200 bg-red-50' 
              : 'border-gray-100 bg-gray-50/50'
            }
          `}
        >
          <div className={`
            p-2 rounded-lg
            ${med.is_overdue ? 'bg-red-100' : 'bg-blue-100'}
          `}>
            <Pill className={`w-5 h-5 ${med.is_overdue ? 'text-red-500' : 'text-blue-500'}`} />
          </div>
          
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="font-medium text-navy text-sm truncate">
                {med.name}
              </span>
              {med.is_overdue && (
                <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
              )}
            </div>
            <p className="text-xs text-warm-gray">
              {med.dosage} • {med.frequency}
            </p>
            {med.next_dose && (
              <div className="flex items-center gap-1 mt-1 text-xs text-gray-400">
                <Clock className="w-3 h-3" />
                Next: {med.next_dose}
              </div>
            )}
          </div>

          <button
            className={`
              p-2 rounded-full transition-colors
              ${med.is_overdue
                ? 'bg-red-200 hover:bg-red-300 text-red-700'
                : 'bg-sage/20 hover:bg-sage/30 text-sage'
              }
            `}
            title="Mark as taken"
          >
            <CheckCircle2 className="w-5 h-5" />
          </button>
        </div>
      ))}
    </div>
  );
}

export default MedicationReminderWidget;
